import React, { createContext, useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export interface Step {
  id: number;
  key: string;
  label: string;
  component: React.FC<any>;
  hideInStepper?: boolean;
}

export interface StepperContextProps {
  activeStep: number;
  steps: Step[];
  // navigation
  next: () => void;
  back: () => void;
  goTo: (step: number) => void;
  navigateTo: (identifier: number | string) => void;
  reset: () => void;
  complete: (step?: number) => void;

  // step helpers
  isCompleted: (step: number) => boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  progress: number;

  // step management
  setSteps: (steps: Step[]) => void;
}

export const StepperContext = createContext<StepperContextProps | undefined>(undefined);

export const StepperProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<Record<number, boolean>>({});
  const [steps, setSteps] = useState<Step[]>([]);

  const totalSteps = steps.length;

  // --- URL → State sync
  useEffect(() => {
    const stepKey = searchParams.get("step");
    if (!stepKey || steps.length === 0) return;

    const idx = steps.findIndex((s) => s.key === stepKey);
    if (idx !== -1) setActiveStep(idx);
  }, [searchParams, steps]);

  // Helper to push key to URL
  const updateURL = (stepIndex: number) => {
    const key = steps[stepIndex]?.key;
    if (key) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("step", key);
      navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
  };

  const goTo = useCallback((step: number) => {
    if (totalSteps === 0) return;
    const safe = Math.max(0, Math.min(step, totalSteps - 1));
    setActiveStep(safe);
    updateURL(safe);
  }, [totalSteps, steps]);

  const navigateTo = useCallback((identifier: number | string) => {
    if (totalSteps === 0) return;
    
    let stepIndex: number;
    
    if (typeof identifier === 'number') {
      // Navigate by id
      stepIndex = steps.findIndex(step => step.id === identifier);
    } else {
      // Navigate by key
      stepIndex = steps.findIndex(step => step.key === identifier);
    }
    
    if (stepIndex !== -1) {
      setActiveStep(stepIndex);
      updateURL(stepIndex);
    }
  }, [totalSteps, steps]);

  const next = useCallback(() => goTo(activeStep + 1), [activeStep, goTo]);
  const back = useCallback(() => goTo(activeStep - 1), [activeStep, goTo]);

  const reset = useCallback(() => {
    setActiveStep(0);
    setCompleted({});
    updateURL(0);
  }, [steps]);

  const complete = useCallback((step?: number) => {
    const s = step ?? activeStep;
    setCompleted((prev) => ({ ...prev, [s]: true }));
  }, [activeStep]);

  const isCompleted = useCallback((step: number) => !!completed[step], [completed]);
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === totalSteps - 1;
  const progress = totalSteps ? (Object.keys(completed).length / totalSteps) * 100 : 0;

  const value = useMemo(() => ({
    activeStep,
    steps,
    next,
    back,
    goTo,
    navigateTo,
    reset,
    complete,
    isCompleted,
    isFirstStep,
    isLastStep,
    progress,
    setSteps,
  }), [activeStep, steps, next, back, goTo, navigateTo, reset, complete, isCompleted, isFirstStep, isLastStep, progress]);

  return <StepperContext.Provider value={value}>{children}</StepperContext.Provider>;
};
